import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { fmtEUR, fmtDateShort } from "../lib/format";
import { AlertTriangle, Users, Lock, ArrowRight, Building2, User, Calendar } from "lucide-react";

export default function DashboardWidgets() {
  const [expiring, setExpiring] = useState([]);
  const [top, setTop] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/expiring-shares").then((r) => setExpiring(r.data)).catch(() => setExpiring([])),
      api.get("/dashboard/top-clients").then((r) => setTop(r.data)).catch(() => setTop([])),
    ]).finally(() => setLoaded(true));
  }, []);

  if (!loaded) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Alert Scadenza Link */}
      <div className="border border-neutral-200 bg-white" data-testid="widget-expiring-shares">
        <div className="p-4 border-b border-neutral-200 flex items-center gap-2">
          <AlertTriangle className={`h-4 w-4 ${expiring.length > 0 ? "text-orange-500" : "text-neutral-300"}`} />
          <div className="flex-1">
            <div className="mono-label">Link condivisi in scadenza</div>
            <h3 className="font-display text-base font-medium">
              {expiring.length > 0 ? `${expiring.length} link scade entro 3 giorni` : "Tutti i link attivi"}
            </h3>
          </div>
        </div>
        <div className="divide-y divide-neutral-100 max-h-80 overflow-y-auto">
          {expiring.length === 0 ? (
            <div className="p-6 text-center text-xs text-neutral-500">
              Nessun link condiviso scade nei prossimi 3 giorni.
            </div>
          ) : expiring.map((e) => {
            const dayLabel =
              e.hours_left < 24 ? `${e.hours_left}h` :
              e.hours_left < 48 ? "domani" :
              `${Math.floor(e.hours_left / 24)}g`;
            const urgent = e.hours_left < 24;
            return (
              <Link
                key={e.token}
                to={`/projects/${e.project_id}`}
                data-testid={`expiring-${e.token}`}
                className="p-3 flex items-center gap-3 hover:bg-neutral-50 group"
              >
                <div className={`px-2 py-1 font-mono text-[10px] font-semibold border ${urgent ? "border-red-300 bg-red-50 text-red-700" : "border-orange-300 bg-orange-50 text-orange-700"}`}>
                  {dayLabel}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-neutral-900 truncate">{e.project_title}</div>
                  <div className="mono-label truncate flex items-center gap-1.5">
                    {e.client_name}
                    {e.has_pin && <Lock className="h-2.5 w-2.5" />}
                  </div>
                </div>
                <ArrowRight className="h-3.5 w-3.5 text-neutral-300 group-hover:text-neutral-700 flex-shrink-0" />
              </Link>
            );
          })}
        </div>
      </div>

      {/* Top Clienti */}
      <div className="border border-neutral-200 bg-white" data-testid="widget-top-clients">
        <div className="p-4 border-b border-neutral-200 flex items-center gap-2">
          <Users className="h-4 w-4 brand-text" />
          <div className="flex-1">
            <div className="mono-label">Clienti più attivi</div>
            <h3 className="font-display text-base font-medium">
              {top.length > 0 ? `Top ${top.length} per gare in corso` : "Nessun cliente attivo"}
            </h3>
          </div>
        </div>
        <div className="divide-y divide-neutral-100">
          {top.length === 0 ? (
            <div className="p-6 text-center text-xs text-neutral-500">Nessuna gara attiva registrata.</div>
          ) : top.map((c, i) => (
            <Link
              key={c.client_id}
              to={`/clients/${c.client_id}`}
              data-testid={`top-client-${c.client_id}`}
              className="p-3 flex items-center gap-3 hover:bg-neutral-50 group"
            >
              <div className="w-7 h-7 flex items-center justify-center brand-bg text-white font-display font-medium text-sm flex-shrink-0">
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-neutral-900 truncate flex items-center gap-1.5">
                  {c.type === "pa" ? <Building2 className="h-3 w-3 text-neutral-500" /> : <User className="h-3 w-3 text-neutral-500" />}
                  {c.name}
                </div>
                <div className="mono-label flex items-center gap-2 mt-0.5">
                  <span><strong className="font-mono font-semibold text-neutral-700">{c.active_count}</strong> gare attive</span>
                  <span>·</span>
                  <span className="font-mono">{fmtEUR(c.total_parcella)}</span>
                  {c.next_deadline && (
                    <>
                      <span>·</span>
                      <span className="flex items-center gap-0.5"><Calendar className="h-2.5 w-2.5" /> {fmtDateShort(c.next_deadline)}</span>
                    </>
                  )}
                </div>
              </div>
              <ArrowRight className="h-3.5 w-3.5 text-neutral-300 group-hover:text-neutral-700 flex-shrink-0" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
